// frontend/src/ts/protocols/persistence/uploadStore.ts
//
// Backend-agnostic upload queue interface. Two implementations:
// - TauriUploadStore: SQLite-backed via NDC Tauri commands.
// - BrowserUploadStore: direct fetch upload (no durable queue).
//
// Mirrors the existing WriteStore pattern (writeStore.ts).

import { isTauriAvailable } from '@/common/utils/tauri'
import {
  PendingUpload,
  UploadSummary,
  DataCollectionStatus,
  WatcherSourceConfig,
} from './uploadTypes'

export interface ClaimUploadsArgs {
  companyId: string
  protocolId: string
  backoffCutoffMs: number
  limit: number
}

export interface GetPendingUploadsArgs {
  companyId: string
  protocolId: string
  stateFilter: string[]
  limit?: number
}

export interface StartUploadWorkerConfig {
  companyId: string
  protocolId: string
  uploadRequestUrl: string
  subjectId: string
  concurrency: number
}

export interface StartDataCollectionConfig {
  companyId: string
  protocolId: string
  /**
   * One or more watched sources, each with its own directory,
   * recursion flag, extension allowlist, file-age threshold, and
   * backup-on-copy flag. Per-source settings are preserved through
   * the IPC; the watcher applies each source's config independently.
   */
  sources: WatcherSourceConfig[]
  protocolStepId?: string
}

export interface StartBrowserUploadArgs {
  companyId: string
  protocolId: string
  filename: string
  file: File                  // original file reference for streaming PUT (browser store)
  subjectId: string          // required for the upload request
  protocolStepId?: string    // link the uploaded file to this protocol step
}

export interface StartBrowserUploadResult {
  uploadId: string
}

export interface FinalizeBrowserUploadArgs {
  companyId: string
  protocolId: string
  uploadId: string
  filename: string
  fileSize: number
  file: File                  // original file reference for streaming PUT (browser store)
  subjectId: string          // required for the upload request
  protocolStepId?: string
  onProgress?: (loaded: number, total: number) => void
  /** Cancels the underlying presign/PUT requests when aborted. */
  signal?: AbortSignal
}

export interface AbortBrowserUploadArgs {
  companyId: string
  protocolId: string
  uploadId: string
  filename: string
  fileSize: number
  error: string
}

export interface PickFileOpts {
  allowedExtensions?: string[]
}

export interface EnqueuePickedFileArgs {
  companyId: string
  protocolId: string
  sourcePath: string
  filenameOverride?: string
  protocolStepId?: string
}

/**
 * Browser-mode contract (used by `uploadCoordinator.uploadFileInBrowser`).
 * TauriUploadStore does NOT implement these — Tauri uploads go straight
 * to `enqueue_picked_file` via the picker or native drag/drop.
 */
export interface BrowserUploadShape {
  startBrowserUpload(args: StartBrowserUploadArgs): Promise<StartBrowserUploadResult>
  finalizeBrowserUpload(args: FinalizeBrowserUploadArgs): Promise<number>
  abortBrowserUpload(args: AbortBrowserUploadArgs): Promise<void>
}

export interface UploadStore {
  browserUpload?: BrowserUploadShape
  retryUpload(companyId: string, protocolId: string, id: number): Promise<void>
  retryAllDeadUploads(companyId: string, protocolId: string): Promise<number[]>
  summary(companyId: string, protocolId: string): Promise<UploadSummary>
  getPendingUploads(args: GetPendingUploadsArgs): Promise<PendingUpload[]>
  getDataCollectionStatus(companyId: string, protocolId: string): Promise<DataCollectionStatus>
  startUploadWorker(args: StartUploadWorkerConfig): Promise<void>
  stopUploadWorker(companyId: string, protocolId: string): Promise<void>
  startDataCollection(args: StartDataCollectionConfig): Promise<void>
  stopDataCollection(companyId: string, protocolId: string): Promise<void>

  /** Open native file picker (Tauri mode only). Returns absolute path or null. */
  pickFile?(opts?: PickFileOpts): Promise<string | null>

  /** Copy a file from absolute path into the durable local store and enqueue it. Tauri mode only. */
  enqueuePickedFile?(args: EnqueuePickedFileArgs): Promise<number>

  /**
   * Enqueue several picked files at once (Tauri mode native drag/drop,
   * multi-select picker, etc.). Each file is copied into the persistence
   * dir via the same Rust `enqueue_picked_file` command as the singular
   * `enqueuePickedFile`. Returns the new row ids in input order.
   */
  enqueuePickedFiles?(files: EnqueuePickedFileArgs[]): Promise<number[]>
}

export class UploadStoreNotAvailableError extends Error {
  constructor() {
    super('UploadStore not available in this environment')
    this.name = 'UploadStoreNotAvailableError'
  }
}

let cached: UploadStore | null = null

export function getUploadStore(): UploadStore {
  if (cached !== null) return cached
  cached = isTauriAvailable() ? new TauriUploadStore() : new BrowserUploadStore()
  return cached
}

export function __setUploadStoreForTests(store: UploadStore | null): void {
  cached = store
}

import { TauriUploadStore } from './tauriUploadStore'
import { BrowserUploadStore } from './browserUploadStore'
